import React, { useContext, useEffect, useState } from 'react';
import { CheckboxField } from '@trp-beacon-ui-kit/react-controls';
import styles from './RPSOCRBeneficiaryInfo.css';
import { FlowContext } from '../../core/flow-engine';
import {
  beneSummaryLine2,
  beneSummaryLine1,
  beneSummaryLine3,
  beneInformationalText,
  beneCheckboxSubText,
  errorInOCRBene,
  isBeneficiariesPresent,
  getOCRBeneSectionText
} from './util';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';
import { randomKey } from '../../core/util';
import RPSOCRRenderPrimaryBeneBox from './RPSOCRRenderPrimaryBeneBox';
import RPSOCRRenderSecondaryBeneBox from './RPSOCRRenderSecondaryBeneBox';

export const RenderBeneInfo = ({ beneficiaries = [], mode }) => {
  const beneLabel = mode === 'Primary-Bene' ? 'Primary' : 'Secondary';
  return (
    beneficiaries.length > 0 && (
      <div className="review-bene-information">
        <div className="bene-header-review">{beneLabel} Beneficiaries</div>
        <div className="review-bene-info">
          {beneficiaries.map(person => {
            return (
              <div key={randomKey()} className="bene-information-lines">
                <div>{beneSummaryLine1(person)}</div>
                <div>{beneSummaryLine2(person)}</div>
                <div>{beneSummaryLine3(person)}</div>
              </div>
            );
          })}
        </div>
        <style jsx>{styles}</style>
      </div>
    )
  );
};

export const ReviewBeneInfo = ({
  primaryBeneficiaries,
  secondaryBeneficiaries,
  primaryBeneficiaryInfo,
  secondaryBeneficiaryInfo,
  beneInfoValid
}) => {
  const isBeneInfoPresent = isBeneficiariesPresent(
    primaryBeneficiaryInfo,
    secondaryBeneficiaryInfo
  );

  const renderPresentBenes = () => (
    <>
      <RenderBeneInfo
        beneficiaries={primaryBeneficiaries}
        mode="Primary-Bene"
      />
      <RenderBeneInfo beneficiaries={secondaryBeneficiaries} />
    </>
  );

  const renderDefaultBenes = () =>
    beneInfoValid ? (
      <div className="review-bene-information">
        No beneficiaries are being added to the account.
      </div>
    ) : (
      <div className="review-bene-information">
        You have not added any Beneficiary details
      </div>
    );

  return (
    <>
      {isBeneInfoPresent && renderPresentBenes()}
      {!isBeneInfoPresent && renderDefaultBenes()}
    </>
  );
};

const shouldValidate = (editBeneInfo, beneInfoValid, isErrorInBeneSection) =>
  (editBeneInfo || beneInfoValid) && !isErrorInBeneSection;

const RPSOCRBeneficiaryInfo = ({
  editBeneInfo,
  setEditBeneInfo,
  showPrimaryBeneRelationshipError,
  setShowPrimaryBeneRelationshipError,
  showPrimaryBeneDistributionError,
  setShowPrimaryBeneDistributionError,
  showSecondaryBeneRelationshipError,
  setShowSecondaryBeneRelationshipError,
  showSecondaryBeneDistributionError,
  setShowSecondaryBeneDistributionError
}) => {
  const {
    next,
    formProps,
    appState: {
      store: {
        loadedData: {
          primaryBeneficiaries = [],
          secondaryBeneficiaries = [],
          primaryBeneficiaryInfo,
          secondaryBeneficiaryInfo,
          beneInfoValid,
          iiBeneDataPresent,
          rpsBeneDataPresent,
          beneInfoEditMode
        }
      }
    }
  } = useContext(FlowContext);

  const [shouldShowBeneficiary, setShouldShowBeneficiary] = useState(
    primaryBeneficiaries.length > 0
  );

  const [
    shouldShowSecondaryBeneficiary,
    setShouldShowSecondaryBeneficiary
  ] = useState(secondaryBeneficiaries.length > 0);

  useEffect(() => {
    setEditBeneInfo(beneInfoEditMode);
  }, [beneInfoEditMode]);

  useEffect(() => {
    setShouldShowBeneficiary(Boolean(primaryBeneficiaries.length));
  }, [primaryBeneficiaries.length]);

  useEffect(() => {
    setShouldShowSecondaryBeneficiary(Boolean(secondaryBeneficiaries.length));
  }, [secondaryBeneficiaries.length]);

  const handleSecondaryBeneSection = () => {
    return setShouldShowSecondaryBeneficiary(!shouldShowSecondaryBeneficiary);
  };

  const isErrorInBeneSection = errorInOCRBene(
    showPrimaryBeneRelationshipError,
    showPrimaryBeneDistributionError,
    showSecondaryBeneRelationshipError,
    showSecondaryBeneDistributionError
  );

  const isValid = shouldValidate(
    editBeneInfo,
    beneInfoValid,
    isErrorInBeneSection
  );

  const handleClick = async () => {
    if (editBeneInfo) {
      await next({
        section: 'ocrBeneficariesInfo'
      })();
    } else {
      await next('unlockRPSBeneSection')();
    }
  };

  const handleBeneficiarySection = () => {
    setShouldShowBeneficiary(!shouldShowBeneficiary);
  };

  const beneSectionText = getOCRBeneSectionText(
    iiBeneDataPresent,
    rpsBeneDataPresent
  );

  return (
    <div className="review-portfolio">
      <div className="section-header">
        <SectionHeading text={beneSectionText} />
        <div className="bene-info-text">{beneInformationalText}</div>
      </div>
      <div className="section-content">
        {editBeneInfo ? (
          <>
            <CheckboxField
              title="I would like to add beneficiary Information now (you can always add this later)."
              onClick={handleBeneficiarySection}
              {...formProps.primaryBeneficiaryInfo}
              checked={shouldShowBeneficiary}
            />
            <div className="bene-sub-text">{beneCheckboxSubText}</div>
            {shouldShowBeneficiary && (
              <>
                <div className="bene-header">Primary Beneficiaries</div>
                <RPSOCRRenderPrimaryBeneBox
                  shouldShowBeneficiary={shouldShowBeneficiary}
                  setShouldShowBeneficiary={setShouldShowBeneficiary}
                  primaryBeneficiaries={primaryBeneficiaries}
                  showPrimaryBeneRelationshipError={
                    showPrimaryBeneRelationshipError
                  }
                  setShowPrimaryBeneRelationshipError={
                    setShowPrimaryBeneRelationshipError
                  }
                  showPrimaryBeneDistributionError={
                    showPrimaryBeneDistributionError
                  }
                  setShowPrimaryBeneDistributionError={
                    setShowPrimaryBeneDistributionError
                  }
                />
                <div className="secondary-bene-checkbox">
                  <CheckboxField
                    title="I would like to add secondary beneficiary Information now (at least one Primary Beneficiary is required)."
                    onClick={handleSecondaryBeneSection}
                    {...formProps.secondaryBeneficiaryInfo}
                    checked={shouldShowSecondaryBeneficiary}
                  />
                </div>
                {shouldShowSecondaryBeneficiary && (
                  <>
                    <div className="bene-header">Secondary Beneficiaries</div>
                    <RPSOCRRenderSecondaryBeneBox
                      secondaryBeneficiaries={secondaryBeneficiaries}
                      showSecondaryBeneRelationshipError={
                        showSecondaryBeneRelationshipError
                      }
                      setShowSecondaryBeneRelationshipError={
                        setShowSecondaryBeneRelationshipError
                      }
                      showSecondaryBeneDistributionError={
                        showSecondaryBeneDistributionError
                      }
                      setShowSecondaryBeneDistributionError={
                        setShowSecondaryBeneDistributionError
                      }
                    />
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <ReviewBeneInfo
            primaryBeneficiaries={primaryBeneficiaries}
            secondaryBeneficiaries={secondaryBeneficiaries}
            primaryBeneficiaryInfo={primaryBeneficiaryInfo}
            secondaryBeneficiaryInfo={secondaryBeneficiaryInfo}
            beneInfoValid={beneInfoValid}
          />
        )}
      </div>
      <EditButton
        onClick={handleClick}
        currentlyEditing={editBeneInfo}
        disabled={!isValid}
      />
      <style jsx>{styles}</style>
    </div>
  );
};

export default RPSOCRBeneficiaryInfo;
